import * as React from 'react'
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import type { Json } from '@lib/helpers/json'
import { ImageField } from './ImageField'
import {
  appendAt,
  getList,
  getString,
  moveAt,
  pathKey,
  removeAt,
  setAt,
  type Draft,
  type DraftProblems,
  type Path,
} from './draft'

/**
 * What every editor below needs: the document, how to change it, and what the
 * schema said about it.
 *
 * Passed as one object rather than threaded field by field, because every editor
 * needs all three and a form this size would otherwise be mostly prop-passing.
 */
export interface DraftControl {
  draft: Draft
  onChange: (next: Draft) => void
  problems: DraftProblems
  /**
   * Whose page this is — needed by any field that uploads.
   *
   * On the control rather than passed down, because an image can appear at any
   * depth (a micro-wedding card's picture is three levels in) and threading it
   * through every editor would be the only reason most of them took a prop.
   */
  venueId: string
}

/**
 * What an emptied box should leave behind.
 *
 * An optional field must be **absent**, not blank: the save schema is strict and
 * the contract reads presence as the switch — `bookingPrivate: ''` renders a
 * dead button where leaving it out shows "coming soon". A required field keeps
 * its empty string, which every text rule here allows.
 */
function cleared(value: string, optional: boolean): string | undefined {
  return value === '' && optional ? undefined : value
}

/** The schema's complaint about exactly this path, if it made one. */
function errorAt(problems: DraftProblems, path: Path): string | undefined {
  return problems.byPath.get(pathKey(path))?.join(' ')
}

/** A single line of text. */
export function TextField({
  control,
  path,
  label,
  hint,
  placeholder,
  optional = false,
}: {
  control: DraftControl
  path: Path
  label: string
  hint?: string
  placeholder?: string
  /** Emptying an optional field removes it; a required one keeps an empty string. */
  optional?: boolean
}): React.ReactElement {
  const id = React.useId()
  const error = errorAt(control.problems, path)
  return (
    <Field
      label={label}
      htmlFor={id}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
    >
      <Input
        id={id}
        value={getString(control.draft, path)}
        placeholder={placeholder ?? ''}
        onChange={(event) => {
          control.onChange(setAt(control.draft, path, cleared(event.target.value, optional)))
        }}
      />
    </Field>
  )
}

/** A paragraph. */
export function TextAreaField({
  control,
  path,
  label,
  hint,
  rows,
  optional = false,
}: {
  control: DraftControl
  path: Path
  label: string
  hint?: string
  rows?: number
  optional?: boolean
}): React.ReactElement {
  const id = React.useId()
  const error = errorAt(control.problems, path)
  return (
    <Field
      label={label}
      htmlFor={id}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
    >
      <Textarea
        id={id}
        rows={rows ?? 3}
        value={getString(control.draft, path)}
        onChange={(event) => {
          control.onChange(setAt(control.draft, path, cleared(event.target.value, optional)))
        }}
      />
    </Field>
  )
}

/** The shared frame for every list: a heading, the rows, and an add button. */
function ListFrame({
  label,
  hint,
  error,
  addLabel,
  onAdd,
  children,
}: {
  label: string
  hint?: string
  error?: string
  addLabel: string
  onAdd: () => void
  children: React.ReactNode
}): React.ReactElement {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-medium text-ink">{label}</span>
        <Button type="button" variant="ghost" size="sm" onClick={onAdd}>
          <Plus className="mr-1 size-3.5" aria-hidden />
          {addLabel}
        </Button>
      </div>
      {hint !== undefined && error === undefined ? (
        <p className="text-xs text-ink-muted">{hint}</p>
      ) : null}
      {error !== undefined ? <FormError>{error}</FormError> : null}
      {children}
    </div>
  )
}

/** The up/down/remove controls every list row carries. */
function RowControls({
  label,
  index,
  count,
  onMove,
  onRemove,
}: {
  label: string
  index: number
  count: number
  onMove: (delta: number) => void
  onRemove: () => void
}): React.ReactElement {
  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Move ${label} ${String(index + 1)} up`}
        disabled={index === 0}
        onClick={() => {
          onMove(-1)
        }}
      >
        <ArrowUp className="size-3.5" aria-hidden />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Move ${label} ${String(index + 1)} down`}
        disabled={index === count - 1}
        onClick={() => {
          onMove(1)
        }}
      >
        <ArrowDown className="size-3.5" aria-hidden />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label={`Remove ${label} ${String(index + 1)}`}
        onClick={onRemove}
      >
        <Trash2 className="size-3.5 text-danger-ink" aria-hidden />
      </Button>
    </div>
  )
}

/** A list of plain strings — "what's included", a list of activities. */
export function StringListField({
  control,
  path,
  label,
  hint,
  itemLabel,
  placeholder,
}: {
  control: DraftControl
  path: Path
  label: string
  hint?: string
  itemLabel: string
  placeholder?: string
}): React.ReactElement {
  const items = getList(control.draft, path)
  const error = errorAt(control.problems, path)
  return (
    <ListFrame
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
      addLabel={`Add ${itemLabel}`}
      onAdd={() => {
        control.onChange(appendAt(control.draft, path, ''))
      }}
    >
      {items.length === 0 ? (
        <p className="text-sm text-ink-muted">Nothing here yet.</p>
      ) : null}
      {items.map((_item, index) => {
        const itemPath = [...path, index]
        const itemError = errorAt(control.problems, itemPath)
        return (
          <div key={pathKey(itemPath)} className="space-y-1">
            <div className="flex items-center gap-2">
              <Input
                size="sm"
                aria-label={`${itemLabel} ${String(index + 1)}`}
                value={getString(control.draft, itemPath)}
                placeholder={placeholder ?? ''}
                onChange={(event) => {
                  control.onChange(setAt(control.draft, itemPath, event.target.value))
                }}
              />
              <RowControls
                label={itemLabel}
                index={index}
                count={items.length}
                onMove={(delta) => {
                  control.onChange(moveAt(control.draft, path, index, delta))
                }}
                onRemove={() => {
                  control.onChange(removeAt(control.draft, path, index))
                }}
              />
            </div>
            {itemError !== undefined ? (
              <FormError>{itemError}</FormError>
            ) : null}
          </div>
        )
      })}
    </ListFrame>
  )
}

/** One named field inside a repeating object. */
export interface ObjectField {
  key: string
  label: string
  /** A paragraph rather than a line. */
  multiline?: boolean
  /** An address with a preview, an upload and the venue's other images. */
  image?: boolean
}

/** A list of objects with the same named fields — price cards, add-on rows. */
export function ObjectListField({
  control,
  path,
  label,
  hint,
  itemLabel,
  fields,
}: {
  control: DraftControl
  path: Path
  label: string
  hint?: string
  itemLabel: string
  fields: readonly ObjectField[]
}): React.ReactElement {
  const items = getList(control.draft, path)
  const error = errorAt(control.problems, path)
  const blank: Json = Object.fromEntries(fields.map((field) => [field.key, '']))
  return (
    <ListFrame
      label={label}
      {...(hint === undefined ? {} : { hint })}
      {...(error === undefined ? {} : { error })}
      addLabel={`Add ${itemLabel}`}
      onAdd={() => {
        control.onChange(appendAt(control.draft, path, blank))
      }}
    >
      {items.length === 0 ? (
        <p className="text-sm text-ink-muted">Nothing here yet.</p>
      ) : null}
      {items.map((_item, index) => {
        const itemPath = [...path, index]
        return (
          <Card
            key={pathKey(itemPath)}
            className="space-y-3 p-3"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-ink-muted">
                {itemLabel} {index + 1}
              </span>
              <RowControls
                label={itemLabel}
                index={index}
                count={items.length}
                onMove={(delta) => {
                  control.onChange(moveAt(control.draft, path, index, delta))
                }}
                onRemove={() => {
                  control.onChange(removeAt(control.draft, path, index))
                }}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {fields.map((field) => (
                <ObjectFieldEditor
                  key={field.key}
                  control={control}
                  path={[...itemPath, field.key]}
                  field={field}
                />
              ))}
            </div>
          </Card>
        )
      })}
    </ListFrame>
  )
}

/** One field of a repeating object, in whichever editor its kind calls for. */
function ObjectFieldEditor({
  control,
  path,
  field,
}: {
  control: DraftControl
  path: Path
  field: ObjectField
}): React.ReactElement {
  if (field.image === true) {
    return <ImageField control={control} path={path} label={field.label} optional />
  }
  if (field.multiline === true) {
    return <TextAreaField control={control} path={path} label={field.label} rows={2} />
  }
  return <TextField control={control} path={path} label={field.label} />
}
